import React from 'react';
import styled, { css } from 'styled-components';
import Divider from '~/components/atom/Divider';
import Icon from '~/components/atom/Icon';
import JustificationContainer from '~/components/atom/JustificationContainer';
import ProgressiveImgIx from '~/components/atom/ProgressiveImgIx';
import { Label, Variant } from '~/components/atom/Typography';

export const IMAGE_CARD_SIZE = 150;

export type Props = {
  className?: string;
  /** Whether the image is selected */
  selected: boolean;
  /** Image source */
  src: string;
  /** Image label */
  label: string;
  /** Callback to handle click on the card */
  onClick: () => void;
};

const ImageCard: React.FCC<Props> = ({ src, label, selected, onClick }) => (
  <Container
    onClick={onClick}
    $selected={selected}
    direction="column"
    align="center"
  >
    <LabelContainer>
      <JustificationContainer
        align="center"
        justification="space-between"
        gap="xxs"
        width="100%"
        padding={['xxs', 'xs']}
        backgroundColor={{ group: 'white' }}
      >
        <Label size="xs" variant={Variant.secondary} margin={[null]}>
          {label}
        </Label>
      </JustificationContainer>
      <Divider
        margin={[null]}
        color={{
          group: 'tertiary',
          variant: selected ? 'base' : 'light',
        }}
      />
    </LabelContainer>

    <ImageContainer
      padding={['xxs']}
      align="center"
      justification="center"
      backgroundColor={{
        group: 'tertiary',
        variant: 'translucent',
      }}
    >
      <ProgressiveImgIx src={src} alt={label} width={120} />
      <Overlay $selected={selected} align="center" justification="center">
        {selected && (
          <Icon
            name="check"
            size="m"
            background={{ group: 'accent', variant: 'base' }}
            color={{ group: 'white', variant: 'base' }}
            strokeWidth={2.2}
          />
        )}
      </Overlay>
    </ImageContainer>
  </Container>
);

const Container = styled(JustificationContainer)<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    cursor: pointer;
    border-radius: ${theme.getTokens().border.radius.m};
    outline: 1px solid ${theme.color('tertiary', 'dark')};
    width: ${IMAGE_CARD_SIZE}px;
    height: ${IMAGE_CARD_SIZE}px;
    transition: all 0.1s ease-in-out;
    overflow: hidden;

    &:hover {
      outline: 2px solid ${theme.color('primary', 'base')};
      transform: translateY(-2px);
    }

    ${$selected &&
    css`
      outline: 2px solid ${theme.color('primary', 'base')};
    `}
  `,
);

const LabelContainer = styled.div`
  width: 100%;
`;

const ImageContainer = styled(JustificationContainer)(
  ({ theme }) => css`
    position: relative;
    border-bottom-right-radius: ${theme.getTokens().border.radius.m};
    border-bottom-left-radius: ${theme.getTokens().border.radius.m};
    width: 100%;
    flex: 1;
    /** 
    * Enables flex item to shrink below content size, allowing 
    * max-height: 100% to work properly on child images
    */
    min-height: 0;

    img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
  `,
);

const Overlay = styled(JustificationContainer)<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${$selected
      ? theme.color('info', 'translucent')
      : 'transparent'};
    transition: background-color 0.2s ease-in-out;
  `,
);

export default ImageCard;
